import React from 'react';
import { Card, Space, Tag, Typography } from 'antd';
import { CalculatorOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatLocalizedPercent } from '../../../utils/numberFormatting';
import {
  getOrderMarginSummary,
  hasOrderCostModelMargin,
  OrderMarginFacts,
} from '../orderMarginSummary';
import './OrderCostModelMarginSection.css';

interface Props {
  order: OrderMarginFacts;
  canViewMargin: boolean;
}

/**
 * The order's margin from the full backend cost model, on the order-information
 * tab. It exists because a LEGACY order has no batches, so the FIFO preview and
 * the warehouse valuation can only render "—" for it; this is the number the
 * profitability report shows. The finances tab renders the same facts through
 * the same `orderMarginSummary` helper.
 *
 * Visibility depends on `canViewMargin` alone — deliberately not on the invoice
 * workflow flag or warehouse-document access, which gate the warehouse section.
 */
export default function OrderCostModelMarginSection({ order, canViewMargin }: Props) {
  const { t, i18n } = useTranslation(['orders']);
  if (!canViewMargin || !hasOrderCostModelMargin(order)) return null;

  const summary = getOrderMarginSummary(order);
  const money = (value: number) => formatCurrencyLocalized(value, 'PLN', i18n.language);
  const percentLabel = summary.percentUnavailableKey
    ? t(summary.percentUnavailableKey)
    : formatLocalizedPercent(summary.percent, i18n.language);

  return <Card
    className="order-cost-model-margin"
    title={<Space><CalculatorOutlined />{t('orders:orderDetail.costModelMarginTitle')}</Space>}
    extra={order.costBasis === 'LEGACY'
      ? <Tag color="gold">{t('orders:orderDetail.costModelMarginEstimated')}</Tag>
      : undefined}
  >
    <div className="order-cost-model-margin__metrics">
      <div className="order-cost-model-margin__metric">
        <span className="order-cost-model-margin__label">{t('orders:orderDetail.totalCost')}</span>
        <strong className="order-cost-model-margin__value" data-testid="cost-model-margin-total-cost">{money(summary.totalCost)}</strong>
      </div>
      <div className={`order-cost-model-margin__metric order-cost-model-margin__metric--result order-cost-model-margin__metric--${summary.tone}`}>
        <span className="order-cost-model-margin__label">{t('orders:orderDetail.orderResult')}</span>
        <strong className="order-cost-model-margin__value" data-testid="cost-model-margin-result">{money(summary.margin)}</strong>
        <span className="order-cost-model-margin__meta">{t(`orders:orderDetail.${summary.statusKey}`)}</span>
      </div>
      <div className="order-cost-model-margin__metric">
        <span className="order-cost-model-margin__label">{t('orders:orderDetail.margin')}</span>
        <strong className="order-cost-model-margin__value" data-testid="cost-model-margin-percent">{percentLabel}</strong>
      </div>
    </div>
    <Typography.Text type="secondary">{t('orders:orderDetail.costModelMarginHelp')}</Typography.Text>
  </Card>;
}
